<template>
  <v-card class="mt-6">
    <!-- ! Policy Number and status -->
    <TerminatedPolicyCardTopBar :contractId="policyData.contractId"></TerminatedPolicyCardTopBar>
    <v-container class="pa-0">
      <v-row>
        <!-- Reinstatement body -->
        <v-col cols="12" class="align-center" v-if="isTerminatedAndEligibleForReinstatement">
          <div>
            <div v-if="isEligibleForFirstTimeReinstatement && !reinstatementRedirectUrlExists">
              <OnexAlert :type="Notifications.error" :icon="mdiAlertCircle">
                {{ $t('L_term_life_policy_terminated_header') }}
                <template #description>{{ $t('L_term_life_policy_terminated_message') }}</template>
                <template #cta>
                  <OnexButton @click="navigateToReinstatement" large color="primary">
                    {{ $t('L_reinstate_policy') }}
                  </OnexButton>
                </template>
              </OnexAlert>
            </div>
            <div v-else-if="reinstatementRedirectUrlExists">
              <OnexAlert :type="Notifications.error" :icon="mdiAlertCircle">
                {{ $t('L_you_have_a_reinstatement_application_in_progress') }}
                <template #cta>
                  <OnexButton @click="goToTheReinstatementUrl" large color="primary">
                    {{ $t('L_complete_and_submit') }}
                  </OnexButton>
                </template>
              </OnexAlert>
            </div>
            <div
              v-else-if="isNotEligibleReinstatementInProgress && !reinstatementRedirectUrlExists"
              class="ma-4"
            >
              <TerminatedProcessedForReinstatementContent />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mdiAlertCircle, mdiMinusCircle } from '@mdi/js';
import TerminatedPolicyCardTopBar from './TerminatedPolicyCardTopBar.vue';
import TerminatedProcessedForReinstatementContent from './TerminatedProcessedForReinstatementContent.vue';
import { mapStores } from 'pinia';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model';
import { RouteNames } from '@/router/routeNames';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { Notifications, OnexAlert, OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'TerminatedPolicyCard',
  mixins: [openInNewWindow],
  components: {
    TerminatedPolicyCardTopBar,
    TerminatedProcessedForReinstatementContent,
    OnexAlert,
    OnexButton,
  },
  data: () => ({
    status: false,
    showPayInProgressTooltip: false,
    mdiAlertCircle,
    mdiMinusCircle,
    Notifications,
  }),
  props: {
    policyData: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore),
    customerServicePhoneNum() {
      if (this.globalStore.getLocale === 'en_US') {
        return '(800) 257-4725';
      } else if (this.globalStore.getLocale === 'en_CA') {
        return '(800) 387-7876';
      } else if (this.globalStore.getLocale === 'fr_CA') {
        return '(800) 265-4804';
      }
      return '(800) 257-4725';
    },
    isTerminatedAndEligibleForReinstatement() {
      return !!this.policyData?.reinstatement;
    },
    isEligibleForReinstatement() {
      return this.policyData?.reinstatement && this.policyData?.reinstatement?.eligible === true;
    },
    isNotEligibleReinstatementInProgress() {
      return this.policyData?.reinstatement?.eligible === false || false;
    },
    isEligibleForFirstTimeReinstatement() {
      return this.policyData?.reinstatement?.eligible === true || false;
    },
    reinstatementRedirectUrlExists() {
      return (
        this.policyData?.reinstatement?.redirectUrl !== undefined &&
        this.policyData?.reinstatement?.redirectUrl !== ''
      );
    },
    reinstatementRedirectUrl() {
      return this.policyData?.reinstatement?.redirectUrl;
    },
    policyList() {
      return this.policyStore.policies.map((p: IPolicyStorePolicy) => ({
        contractId: p.contractId,
      }));
    },
    policyStatusIconColor() {
      return 'error';
    },
    policyStatusIcon() {
      return 'error';
    },
  },
  methods: {
    async navigateToReinstatement() {
      try {
        // track in dynatrace
        const i = DynatraceClient.enterAction('Reinstatement_StartAction');
        DynatraceClient.leaveAction(i);
        const tempIndex = this.policyStore.policies.findIndex(
          (policy: IPolicyStorePolicy) => policy.contractId === this.policyData.contractId
        );
        this.policyStore.selectedPolicyIndex = tempIndex;
        // track in cp activity log
        await this.globalStore.postAuditLog({ actionId: 'Reinstatement_StartAction' });
      } catch (_error) {
        /* empty */
      }
      await this.$router.push({ name: RouteNames.POLICY_REINSTATEMENT });
    },
    async goToTheReinstatementUrl() {
      const isWeb = this.$pc2capacitor.isWeb();
      await this.openInNewWindow({
        url: this.reinstatementRedirectUrl,
        target: '_blank',
        inappbrowser: !isWeb,
      });
    },
  },
};

export default defineComponent(componentOptions);
</script>
